// Discount codes and price notes on the storefront. Config: snippets/sfera-discount.liquid
// (Theme settings → Discount codes). Rules agreed with the review committee (19-oct-2026):
// - Active code: Shopify keeps a code from a /discount/CODE link (or from apply()) in the cart and in
//   the discount_code cookie; BUY NOW's /cart/clear.js keeps it, so it does reach the checkout.
// - Only the welcome code gets an amount, for products of its collection, and the same price in BUY NOW
//   (snippets/main-product__buy-buttons.liquid). In euros and fixed-rate currencies (GBP), with the code
//   really in the cart: the selected card shows its price struck through and the price with the code,
//   and under BUY NOW "€10 code applied" with the amount taken off, in the visitor's currency (Jorge's
//   A+B with V1, 21-oct-2026; review committee: the bubble says it is the code and is seen on the same
//   screen as the card; only with the code in the cart; never two strikethroughs: a variant on sale
//   shows no price with the code). In the
//   other currencies, "≈ … with your code" under the cards, as before. Any other code: a note without
//   an amount.
// - Shipping and the total with shipping are shown by snippets/sfera-shipping-line.liquid, which can
//   take the code off with window.sferaDiscount.discountFor() and the "sfera:discount-changed" event.
// - The welcome amount ("10€") in the popup, the floating button and the bar stays in euros for
//   everyone (Jorge, 20-oct-2026: "10€" sounds better, and it is exact: Shopify takes 10 € off,
//   converted). Theme setting "Show the welcome amount in the visitor's currency" switches back to
//   the visitor's currency, rounded down ("£8").
(() => {
  if (window.sferaDiscount) return;
  const configElement = document.getElementById('SferaDiscountConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const currency = window.Shopify?.currency?.active || 'EUR';
  const rate = Number(window.Shopify?.currency?.rate) || 1;
  const moneyFormat = (window.theme?.settings?.money_format || '€{{amount}}').replace(/<[^>]*>/g, '');
  // Kept in base64 in the page so crawlers and AI search don't read it from the HTML (it is written
  // in plain text only when the code panel is shown).
  const welcomeCode = (() => {
    try {
      return atob(config.welcomeCodeB64 || '').trim();
    } catch (error) {
      return '';
    }
  })();
  const welcomeAmount = Number(config.welcomeAmount) || 0;
  const exactCurrency =
    currency === 'EUR' ||
    String(config.fixedRateCurrencies || '')
      .toUpperCase()
      .split(/[\s,]+/)
      .includes(currency);

  // Same output as Liquid's money_without_trailing_zeros for the current currency.
  const formatMoney = (cents, trimZeros = true) => {
    const group = (decimals, thousands, decimal) => {
      const [integer, fraction] = (cents / 100).toFixed(decimals).split('.');
      const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      if (!fraction) return grouped;
      if (trimZeros && /^0+$/.test(fraction)) return grouped;
      return grouped + decimal + fraction;
    };
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount_no_decimals': return group(0, ',', '.');
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return group(0, ' ', ',');
        case 'amount_with_space_separator': return group(2, ' ', ',');
        case 'amount_with_period_and_space_separator': return group(2, ' ', '.');
        default: return group(2, ',', '.');
      }
    });
  };

  // "€10"; with the local-currency setting, "£8" / "110 kr": rounded down so it never promises more
  // than the real discount.
  const localWelcome = Boolean(config.welcomeLocalCurrency) && currency !== 'EUR';
  const welcomeLabel = () =>
    localWelcome ? formatMoney(Math.floor(welcomeAmount * rate) * 100) : '€' + welcomeAmount;

  const inCollection = collections =>
    Boolean(config.collection) && String(collections || '').split(',').includes(config.collection);

  // The welcome code is a fixed amount in euros, once per customer and not combinable with other
  // product or order discounts (Shopify, 19-oct-2026): Shopify converts it with the
  // store's exchange rate (checked: 8.40 £ off at the manual GBP rate 0.84).
  const welcomeOff = priceCents => Math.min(Math.round(welcomeAmount * 100 * rate), priceCents);

  const getCookie = name => {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  };

  let activeCode = null;
  // True when the cart itself lists the code (not only the discount_code cookie).
  let codeInCart = false;
  const isWelcome = code => Boolean(code) && code === welcomeCode.toUpperCase();

  // Black Friday guard: if the selected variant is on sale (compare-at price), the welcome code may not
  // combine with it, so no price with the code is shown (it would bind us to a price the checkout
  // might not give).
  const selectedCard = () =>
    document.querySelector('product-variant-selects:not([style*="none"]) input:checked + label [data-sfera-price]');
  const onSale = card => Boolean(card) && Number(card.dataset.sferaCompare || 0) > Number(card.dataset.sferaPrice || 0);

  // Cents off a product price (in the visitor's currency) with the active code; 0 if no code, another
  // code, the price display is switched off, the product is not in the code's collection or on sale.
  const discountFor = (priceCents, collections) =>
    isWelcome(activeCode) &&
    config.showCodePrice &&
    welcomeAmount > 0 &&
    inCollection(collections) &&
    !onSale(selectedCard())
      ? welcomeOff(priceCents)
      : 0;

  // The selected card shows the price with the code itself (struck-through price), and "code applied"
  // goes under BUY NOW.
  const onCard = off => off > 0 && exactCurrency && codeInCart;

  // Lines under the variant cards, for the selected variant.
  const noteLines = (priceCents, collections) => {
    const lines = [];
    const approx = exactCurrency ? '' : '≈ ';
    // Approximate amounts in whole units: "≈ 1 644 kr", not "≈ 1 643,66 kr".
    const money = cents => formatMoney(exactCurrency ? cents : Math.round(cents / 100) * 100);

    const off = discountFor(priceCents, collections);
    if (onCard(off)) return lines;
    if (off) {
      lines.push(
        config.strings.codePrice
          .replace('[price]', approx + money(priceCents - off))
          .replace('[discount]', approx + '−' + money(off))
      );
    } else if (activeCode && !isWelcome(activeCode)) {
      lines.push(config.strings.saved.replace('[code]', activeCode));
    }
    return lines;
  };

  // Selected card: "€155" struck through and "€145". Other cards: their own price.
  // Idempotent (the MutationObserver calls render again after our own changes).
  const renderCards = selects => {
    selects.querySelectorAll('label [data-sfera-price]').forEach(price => {
      const selected = Boolean(price.closest('label')?.previousElementSibling?.checked);
      const priceCents = Number(price.dataset.sferaPrice);
      const off = selected ? discountFor(priceCents, price.dataset.sferaCollections) : 0;
      const key = onCard(off) ? String(priceCents - off) : '';
      if ((price.dataset.sferaCode || '') === key) return;
      if (price.dataset.sferaOriginal === undefined) price.dataset.sferaOriginal = price.innerHTML;
      if (!key) {
        price.innerHTML = price.dataset.sferaOriginal;
        delete price.dataset.sferaCode;
        return;
      }
      const box = document.createElement('span');
      box.className = 'sfera-code-price';
      const was = document.createElement('s');
      was.className = 'sfera-code-price__was';
      was.textContent = formatMoney(priceCents);
      const now = document.createElement('span');
      now.className = 'sfera-code-price__now';
      now.textContent = formatMoney(priceCents - off);
      box.append(was, now);
      price.replaceChildren(box);
      price.dataset.sferaCode = key;
    });
  };

  // "✓ €10 code applied", with the amount Shopify takes off in the visitor's currency. Under BUY NOW
  // the shipping line comes first (review committee). On one line with it when the shipping line has a
  // short form: the short notice ("Shipping at checkout."), or in the UK "+£16 delivery" with the total
  // on the next line (Jorge, 21-oct-2026: less height); otherwise, under the shipping line.
  const renderApplied = (selects, off) => {
    const form = selects.closest('product-info')?.querySelector('.product__buy-buttons product-form');
    if (!form) return;
    const slot = form.parentElement.querySelector(':scope > .sfera-ship-slot');
    const oneLine = Boolean(slot?.querySelector('.sfera-ship__short, .sfera-ship__compact'));
    let applied = form.parentElement.querySelector('.sfera-code-applied');
    const text = onCard(off) ? (config.strings.codeApplied || '').replace('[amount]', formatMoney(off)) : '';
    slot?.classList.toggle('sfera-ship-slot--code', Boolean(text) && oneLine);
    if (!text) {
      applied?.remove();
      return;
    }
    if (!applied) {
      applied = document.createElement('p');
      applied.className = 'sfera-code-applied';
      applied.setAttribute('role', 'status');
    }
    if (oneLine) {
      if (slot.firstElementChild !== applied) slot.prepend(applied);
    } else {
      const after = slot || form;
      if (applied.previousElementSibling !== after) after.insertAdjacentElement('afterend', applied);
    }
    if (applied.dataset.text === text) return;
    applied.dataset.text = text;
    applied.innerHTML =
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="8" fill="#ff5314"/>' +
      '<path d="M4.5 8.3l2.2 2.2 4.8-4.8" stroke="#151515" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    applied.append(document.createTextNode(text));
  };

  const render = () => {
    document.querySelectorAll('product-variant-selects').forEach(selects => {
      // A variant change inserts the new picker before the old one, hides the old one and removes
      // it 500 ms later (HTMLUpdateUtility.viewTransition): only the visible picker gets the note.
      if (selects.style.display === 'none') return;
      renderCards(selects);
      const chosen = selects.querySelector('input:checked + label [data-sfera-price]');
      renderApplied(selects, chosen ? discountFor(Number(chosen.dataset.sferaPrice), chosen.dataset.sferaCollections) : 0);
      const notes = selects.parentElement
        ? Array.from(selects.parentElement.children).filter(child => child.classList.contains('sfera-discount-note'))
        : [];
      notes.slice(1).forEach(extra => extra.remove());
      let note = notes[0] || null;

      const selected = selects.querySelector('input:checked + label [data-sfera-price]');
      const lines = selected ? noteLines(Number(selected.dataset.sferaPrice), selected.dataset.sferaCollections) : [];
      if (!lines.length) {
        note?.remove();
        return;
      }

      const text = lines.join('\n');
      if (note && note.dataset.text === text && note.previousElementSibling === selects) return;
      if (!note) {
        note = document.createElement('div');
        note.className = 'sfera-discount-note';
        note.setAttribute('role', 'status');
      }
      if (note.dataset.text !== text) {
        note.dataset.text = text;
        note.replaceChildren(
          ...lines.map(line => {
            const paragraph = document.createElement('p');
            paragraph.textContent = line;
            return paragraph;
          })
        );
      }
      if (note.previousElementSibling !== selects) selects.insertAdjacentElement('afterend', note);
    });
  };

  // The variant picker is re-rendered when the variant changes: render again right away, before
  // the next paint, so the old note never flashes. The observer is paused while we render, so our
  // own changes never call us back.
  let observer = null;
  const watch = () => {
    if (observer) return;
    const options = { childList: true, subtree: true };
    observer = new MutationObserver(() => {
      observer.disconnect();
      try {
        render();
      } finally {
        observer.observe(document.body, options);
      }
    });
    observer.observe(document.body, options);
  };

  const setActive = (code, inCart) => {
    activeCode = code ? code.toUpperCase() : null;
    codeInCart = Boolean(activeCode) && Boolean(inCart);
    // Read from cookies: sent to Clarity only with analytics consent.
    window.sferaTrack?.whenAnalytics(() => window.sferaTrack.set('has_discount', activeCode ? '1' : '0'));
    render();
    if (activeCode) watch();
    document.dispatchEvent(new CustomEvent('sfera:discount-changed', { detail: { code: activeCode } }));
  };

  const pickCode = discountCodes => {
    const codes = (discountCodes || []).map(discount => String(discount.code || '').toUpperCase()).filter(Boolean);
    return codes.find(isWelcome) || codes[0] || null;
  };

  // The cart is the truth: opening the checkout makes Shopify drop the discount_code cookie while the
  // cart keeps the code. Without any cart or code cookie there is nothing to ask.
  const refresh = async () => {
    const cookieCode = getCookie('discount_code');
    if (!cookieCode && !getCookie('cart')) {
      setActive(null);
      return;
    }
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(response => response.json());
      setActive(pickCode(cart.discount_codes), true);
    } catch (error) {
      setActive(cookieCode, false);
    }
  };

  // Same as opening /discount/CODE, without leaving the page.
  const apply = async code => {
    const response = await fetch(`/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart.js')}`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Discount ${response.status}`);
    const cart = await response.json();
    const inCart = pickCode(cart.discount_codes);
    setActive(inCart || code, Boolean(inCart));
    return cart;
  };

  // "10€" in the popup, the floating button and the announcement bar → visitor's currency (only with
  // the local-currency setting).
  const localizeWelcomeAmount = () => {
    if (!welcomeAmount || !localWelcome) return;
    const amount = String(welcomeAmount);
    const pattern = new RegExp('(^|[^\\d.,])(?:' + amount + '\\s?€|€\\s?' + amount + ')(?![\\d.,])', 'g');
    const label = welcomeLabel();
    document.querySelectorAll('[data-sfera-welcome-amount]').forEach(root => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        if (walker.currentNode.nodeValue.includes('€')) nodes.push(walker.currentNode);
      }
      nodes.forEach(node => {
        node.nodeValue = node.nodeValue.replace(pattern, (match, before) => before + label);
      });
    });
  };

  window.sferaDiscount = {
    apply,
    refresh,
    discountFor,
    formatMoney,
    welcomeLabel,
    welcomeCode,
    // EUR and fixed-rate currencies (GBP): amounts with the code are exact; elsewhere they are "≈".
    exactCurrency,
    welcomeUrl: config.welcomeUrl || '/products/sferaboard',
    get activeCode() {
      return activeCode;
    }
  };

  localizeWelcomeAmount();
  refresh();
})();
